import {
  GetString,
} from 'fluent-react';
import noop from 'lodash-es/noop';
import React, {
  RefObject, useEffect, useState,
} from 'react';
import styled from 'styled-components';
import {
  Location,
  Product,
  ProductClass,
  ProductSpaceDatum,
  ProductYear,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import Chart from '../../network/chart';
import Tooltips from '../../network/container/TooltipsContainer';
import {
  getIdentityTransformMatrix, ITransformationMatrix,
} from '../../network/panZoom';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import Loading from '../../sharedComponents/GraphLoading';
import { H4 } from '../../sharedComponents/NewTextStyling';
import { getColorTripletFromColorString } from '../../tree/newChart/domIndependentDrawUtils';
import useLastSuccesfulFetch from '../../tree/newChart/useLastSuccesfulFetch';
import {
  GraphAreaContainer,
} from '../Grid';
import { ExportsChangeNodeSizing } from '../Utils';
import SectorLabels from './SectorLabels';
import transform from './transform';

const query = gql`
  query ProductSpace($year: Int!, $locationID: ID!) {
    location(id: $locationID) {
      id
      shortName
    }
    productSpace(location: $locationID, year: $year, productClass: HS) {
      exportValue
      rca
      x
      y
      product {
        id
        code
        shortName
        longName
        topLevelParent {
          id
          shortName
        }
      }
      connections {
        productId
      }
    }
    productYear: allProductYear(year: $year, productClass: HS, productLevel: fourDigit) {
      product {
        id
      }
      pci
      globalExportValue
    }
  }
`;

const FullSizeContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;
const zIndices = {
  chartAreaMeasurementEl: 10,
  chart: 20,
  chartTitle: 30,
  tooltips: 40,
  spinner: 50,
};
const ChartContainer = styled(FullSizeContainer)`
  z-index: ${zIndices.chart};
`;
const SpinnerContainer = styled(FullSizeContainer)`
  z-index: ${zIndices.spinner};
`;
const TooltipsContainer = styled(FullSizeContainer)`
  z-index: ${zIndices.tooltips};
  pointer-events: none;
`;

const ChartTitle = styled(H4)`
  z-index: ${zIndices.chartTitle};
  position: relative;
  pointer-events: none;
  margin: 0;
  padding-right: 100px;

  @media (max-width: 1080px) {
    font-size: 1rem;
  }

  @media (max-width: 940px) {
    font-size: 0.85rem;
    font-weight: 600;
  }
`;

export interface ProductSpaceResponseDatum {
  product: {
    id: Product['id'];
    code: Product['code'];
    shortName: Product['shortName'];
    longName: Product['longName'];
    topLevelParent: {
      id: Product['topLevelParent']['id'];
      shortName: Product['topLevelParent']['shortName'];
    };
  };
  exportValue: ProductSpaceDatum['exportValue'];
  rca: ProductSpaceDatum['rca'];
  x: ProductSpaceDatum['x'];
  y: ProductSpaceDatum['y'];
  connections: Array<{
    productId: Product['id'];
  }>;
}

export interface ProductYearDatum {
  product: {id: Product['id']};
  pci: ProductYear['pci'];
  globalExportValue: ProductYear['globalExportValue'];
}

export interface ProductSpaceMergedDatum {
  product: {
    id: Product['id'];
    code: Product['code'];
    shortName: Product['shortName'];
    longName: Product['longName'];
    pci: ProductYear['pci'];
    globalExportValue: ProductYear['globalExportValue'];
    topLevelParent: {
      id: Product['topLevelParent']['id'];
      shortName: Product['topLevelParent']['shortName'];
    };
  };
  exportValue: ProductSpaceDatum['exportValue'];
  rca: ProductSpaceDatum['rca'];
  x: ProductSpaceDatum['x'];
  y: ProductSpaceDatum['y'];
  connections: Array<{
    datum: {
      exportValue: ProductSpaceDatum['exportValue'];
      rca: ProductSpaceDatum['rca'];
      x: ProductSpaceDatum['x'];
      y: ProductSpaceDatum['y'];
      product: {
        id: Product['id'];
        code: Product['code'];
        shortName: Product['shortName'];
        longName: Product['longName'];
        pci: ProductYear['pci'];
        globalExportValue: ProductYear['globalExportValue'];
        topLevelParent: {
          id: Product['id'];
        };
      };
    };
  }>;
}

interface SuccessResponse {
  location: null | {
    id: Location['id'],
    shortName: Location['shortName'],
  };
  productSpace: ProductSpaceResponseDatum[];
  productYear: ProductYearDatum[];
}

interface Variables {
  year: number;
  locationID: string;
}

interface ExtraInputs {
  zIndexFromAbove: number;
  rootElRef: RefObject<HTMLDivElement>;
  year: number;
  getFluentString: GetString;
  showAllProducts: boolean;
  showTitle: boolean;
  showSectorLabels: boolean;
  nodeSizing: ExportsChangeNodeSizing;
}

interface DOMMeasurement {
  width: number;
  height: number;
  topOffset: number;
  leftOffset: number;
}

interface ContainerProps {
  variables: Variables;
  extraInputs: ExtraInputs;
}

const Container = (props: ContainerProps) => {
  const {
    extraInputs: {
      zIndexFromAbove, getFluentString, showAllProducts, showTitle, showSectorLabels, nodeSizing,
    },
    extraInputs, variables,
  } = props;

  const {loading, error, data: countryData} = useQuery<SuccessResponse, Variables>(query, {variables});
  const {loading: loading2, error: error2, data: allProductsData} = useQuery<SuccessResponse, Variables>(query, {variables:{...variables,locationID: 'location-840'}});

  const mergedData = React.useMemo(() => {
    if (!countryData || !allProductsData) return countryData;

    const productMap = new Map();
    allProductsData.productSpace.forEach(product => {
      productMap.set(product.product.id, {...product, exportValue: 0, rca: 0});
    });
    countryData.productSpace.forEach(product => {
      productMap.set(product.product.id, product);
    });

    return {
      ...countryData,
      productSpace: Array.from(productMap.values()),
    };
  }, [countryData, allProductsData]);

  const data = mergedData;
  const [domMeasurements, setDOMMeasurements] = useState<DOMMeasurement | undefined>(undefined);
  const [transformationMatrix, setTransformationMatrix] =
    useState<ITransformationMatrix>(getIdentityTransformMatrix());
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [hoveredProduct, setHoveredProduct] = useState<number | undefined>(undefined);
  const onNodeMouseEnter = (id: number) => setHoveredProduct(id);
  const onNodeMouseLeave = () => setHoveredProduct(undefined);
  const onNodeClick = (inputId: number) => {
    const isPartOfSelection = selectedProducts.includes(inputId);
    // If clicked node is part of selection, remove it. Otherwise, add it to
    // selection:
    const newSelectedProducts = isPartOfSelection ?
                                  selectedProducts.filter(id => id !== inputId) :
                                  [...selectedProducts, inputId];
    setSelectedProducts(newSelectedProducts);
  };
  const updateTransformationMatrix = (matrix: ITransformationMatrix) => {
    setTransformationMatrix(() => matrix);
  };

  const onDoubleClick = () => {
    setSelectedProducts([]);
    setHoveredProduct(undefined);
  };

  useEffect(() => {
    const {current: chartAreaMeasurementEl} = extraInputs.rootElRef;
    if (chartAreaMeasurementEl !== null) {
      const {width, height, top, left} = chartAreaMeasurementEl.getBoundingClientRect();
      setDOMMeasurements({
        width: Math.floor(width),
        height: Math.floor(height),
        topOffset: top,
        leftOffset: left,
      });
    }
  }, []);

  const lastSuccesfulFetch = useLastSuccesfulFetch<SuccessResponse, Variables, ExtraInputs>({
    inputs: {queryResult: {loading, error, data, variables}, extraInputs},
    isFetchedDataEmpty: ({location}) => location === null,
  });

  const spinnerKey = 'spinner';
  const chartKey = 'chart';
  const tooltipsKey = 'tooltips';
  const sectorLabelsKey = 'sector-labels';

  // Do notshow zoom/pan buttons if the sector labels are displayed because we
  // want the labels to annotate the product space in the most general sense:
  const shouldShowZoomButtons = !showSectorLabels;

  let output: React.ReactElement<any> | null;
  if (loading === true) {
    if (lastSuccesfulFetch !== undefined && domMeasurements !== undefined) {
      const productMap = new Map();
      const {productSpace: allProductsProductSpace=[]} = allProductsData || {};
      const {productSpace: countryProductSpace=[]} = countryData || {};
      allProductsProductSpace.forEach(product => {
        productMap.set(product.product.id, product);
      });
      countryProductSpace.forEach(product => {
        productMap.set(product.product.id, {...product, exportValue: 0, rca: 0});
      });
      const productSpace = Array.from(productMap.values());

      const {
        data: {
          location: lastSuccessfulLocation,
          productYear,
        },
      } = lastSuccesfulFetch;
      if (lastSuccessfulLocation !== null) {
        const title = showTitle === true ? (
          <ChartTitle>
            {getFluentString('country-product-space-chart-title', {
              'country-name': lastSuccessfulLocation.shortName,
              'year': `${extraInputs.year}`,
            })}
          </ChartTitle>
        ) : null;
        const {
          nodeList, edgeList, relatedNodesMap, tooltipMap,
        } = transform(
          productSpace, productYear, domMeasurements.height, domMeasurements.width, showAllProducts, nodeSizing,
        );
        output = (
          <>
            {title}
            <ChartContainer key={chartKey}>
              <Chart
                nodeList={nodeList}
                edgeList={edgeList}
                saveRootEl={noop}
                relatedNodesMap={relatedNodesMap}
                selectedProducts={selectedProducts}
                highlightedProduct={undefined}
                hoveredProduct={hoveredProduct}
                chartContainerHeight={domMeasurements.height}
                chartContainerWidth={domMeasurements.width}
                onNodeClick={onNodeClick}
                onNodeDoubleClick={onDoubleClick}
                onNodeMouseEnter={onNodeMouseEnter}
                onNodeMouseLeave={onNodeMouseLeave}
                updateTransformationMatrix={updateTransformationMatrix}
                showZoomButtons={shouldShowZoomButtons}
                stringToColorTriplet={getColorTripletFromColorString}
                backgroundColor={{R: 255, G: 255, B: 255}}
                key={chartKey}
              />
            </ChartContainer>
            <TooltipsContainer key={tooltipsKey}>
              <Tooltips
                tooltipMap={tooltipMap} hoveredProduct={hoveredProduct}
                highlightedProduct={undefined} selectedProducts={selectedProducts}
                chartContainerHeight={domMeasurements.height} chartContainerWidth={domMeasurements.width}
                productClass={ProductClass.HS}
                transformationMatrix={transformationMatrix}
                closeTooltip={onNodeClick}
              />
            </TooltipsContainer>
            <SpinnerContainer key={spinnerKey}>
              <Loading/>
            </SpinnerContainer>
          </>
        );
      } else {
        output = (
          <>
            <SpinnerContainer key={spinnerKey}>
              <Loading/>
            </SpinnerContainer>
          </>
        );
      }
    } else {
      output = (
        <>
          <SpinnerContainer key={spinnerKey}>
            <Loading/>
          </SpinnerContainer>
        </>
      );
    }
  } else if (error !== undefined) {
    console.error(error);
    output = null;
  } else if (data !== undefined) {
    const {location, productSpace, productYear} = data;
    if (domMeasurements !== undefined &&
      location !== null) {
      const {
        nodeList, edgeList, relatedNodesMap, tooltipMap,
      } = transform(
        productSpace, productYear, domMeasurements.height, domMeasurements.width, showAllProducts, nodeSizing,
      );
      const title = showTitle === true ? (
        <ChartTitle>
          {getFluentString('country-product-space-chart-title', {
            'country-name': location.shortName,
            'year': `${extraInputs.year}`,
          })}
        </ChartTitle>
      ) : null;
      let sectorLabels: React.ReactElement<any> | null;
      if (showSectorLabels === true) {
        sectorLabels = (
          <ChartContainer key={sectorLabelsKey}>
            <SectorLabels
              products={productSpace}
              chartHeight={domMeasurements.height}
              chartWidth={domMeasurements.width}
            />
          </ChartContainer>
        );
      } else {
        sectorLabels = null;
      }
      output = (
        <>
          {title}
          <ChartContainer key={chartKey}>
              <Chart
                nodeList={nodeList}
                edgeList={[]}
                saveRootEl={noop}
                relatedNodesMap={relatedNodesMap}
                selectedProducts={selectedProducts}
                highlightedProduct={undefined}
                hoveredProduct={hoveredProduct}
                chartContainerHeight={domMeasurements.height}
                chartContainerWidth={domMeasurements.width}
                onNodeClick={onNodeClick}
                onNodeDoubleClick={onDoubleClick}
                onNodeMouseEnter={onNodeMouseEnter}
                onNodeMouseLeave={onNodeMouseLeave}
                updateTransformationMatrix={updateTransformationMatrix}
                showZoomButtons={shouldShowZoomButtons}
                stringToColorTriplet={getColorTripletFromColorString}
                backgroundColor={{R: 255, G: 255, B: 255}}
                key={chartKey}
                />
          </ChartContainer>
          {sectorLabels}
          <TooltipsContainer key={tooltipsKey}>
            <Tooltips
              tooltipMap={tooltipMap} hoveredProduct={hoveredProduct}
              highlightedProduct={undefined} selectedProducts={selectedProducts}
              chartContainerHeight={domMeasurements.height} chartContainerWidth={domMeasurements.width}
              productClass={ProductClass.HS}
              transformationMatrix={transformationMatrix}
              closeTooltip={onNodeClick}
            />
          </TooltipsContainer>
        </>
      );
    } else {
      output = null;
    }
  } else {
    output = null;
  }

  const style: React.CSSProperties = {
    zIndex: zIndexFromAbove,
  };

  return (
    <GraphAreaContainer style={style} ref={extraInputs.rootElRef}>
      {output}
    </GraphAreaContainer>
  );
};

type Props = Omit<ExtraInputs, 'rootElRef'> & SlideElementProps & {
  year: number
  countryId: string,
};
const ProductSpace = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const {
    year, countryId, zIndexFromAbove, getFluentString,
    showAllProducts, showTitle, showSectorLabels, nodeSizing,
  } = props;
  const variables: Variables = {
    year,
    locationID: countryId,
  };
  return (
    <Container
      extraInputs={{
        zIndexFromAbove, rootElRef, year, getFluentString,
        showAllProducts, showTitle, showSectorLabels, nodeSizing,
      }}
      variables={variables}
    />
  );
});

export default ProductSpace;
